import React from 'react';
import * as Yup from 'yup';
import { Row, Col } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import { Formik, FormikProps, prepareDataForValidation } from 'formik';
import { Form } from 'formik-antd';

import Radio from 'components/formik/Radio';
import AssignmentTypeCard from './AssignmentTypeCard';
import FormItem from 'components/formik/FormItem';
import FormikEffect from 'components/formik/FormikEffect';
import { AssignmentTypeEnum } from 'api/backofficeApiClient';

import styles from './AssignmentTypeSelection.module.less';

type Props = {
  formRef: any;
  onChange: (formik: FormikProps<TypeSelectionFormValues>) => void;
  onAssignmentTypeChange: (event: RadioChangeEvent) => void;
  initialValues?: TypeSelectionFormValues;
  disabled?: boolean;
};

export interface TypeSelectionFormValues {
  assignmentType: AssignmentTypeEnum | null;
}

const getDescription = (values: TypeSelectionFormValues) => {
  const { assignmentType } = values;

  switch (assignmentType) {
    case AssignmentTypeEnum.EEAssignment:
      return (
        <>
          <div className={styles.entityDescriptionTitle}>W2</div>
          <div>
            Select this option if your candidate is considered an employee whose
            taxes will be deducted from their earnings. As a W-2, every
            myBasePay employee will be offered our Benefits Package, ie: health
            insurance and PTO. This option will reflect W2 Pricing.
          </div>
        </>
      );

    case AssignmentTypeEnum.ICAssignment:
      return (
        <>
          <div className={styles.entityDescriptionTitle}>IC</div>
          <div>
            Select this option if your candidate is an individual that is
            self-employed and acting as their own business entity who must
            undergo an Independent Contractor classification process. You will
            be requested to complete an IC Member Questionnaire to assist us in
            the proper compliance classification of this worker. Thanks in
            advance for your help in this process! This option will reflect
            Non-W2 Pricing.
          </div>
        </>
      );

    case AssignmentTypeEnum.SCAssignment:
      return (
        <>
          <div className={styles.entityDescriptionTitle}>Subcontract</div>
          <div>
            Select this option if your candidate is employed through an outside
            company who assigns one of those members to work the assignment.
            Many times, these organizations are referred to as “Staff
            Augmentation” agencies. This option will reflect Non-W2 Pricing.
          </div>
        </>
      );

    default:
      return <div>Please select an assignment type.</div>;
  }
};

export const isAssignmentTypeFormValidForDraft = (
  formik: FormikProps<TypeSelectionFormValues>,
) => {
  const dataForValidation = prepareDataForValidation(formik.values);

  return validationSchema.isValidSync(dataForValidation);
};

const validationSchema = Yup.object({
  assignmentType: Yup.number()
    .nullable()
    .required(
      'Please select assignment type to proceed with the assignment setup',
    ),
});

export default React.memo((props: Props) => {
  const {
    formRef,
    onChange,
    onAssignmentTypeChange,
    initialValues: predefinedInitialValues,
    disabled,
  } = props;

  const initialValues: TypeSelectionFormValues = {
    assignmentType: null,
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      onSubmit={() => {}}
      initialValues={predefinedInitialValues || initialValues}
      validationSchema={validationSchema}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />

          <Form className={styles.wrapper}>
            <div className="modal-scroll-wrapper">
              <div className={styles.entityTitle}>
                Please select an assignment type in order to proceed with the
                assignment creation.
              </div>

              <Row gutter={20}>
                <Col xs={24} md={12}>
                  <Row>
                    <Col xs={24}>
                      <FormItem name="assignmentType">
                        <Radio.Group
                          className={styles.radioGroup}
                          name="assignmentType"
                          onChange={onAssignmentTypeChange}
                          disabled={disabled}
                        >
                          <Row gutter={{ xs: 10, sm: 10, md: 20, lg: 20 }}>
                            <Col xs={8}>
                              <Radio.Button
                                className="radio-card-button"
                                value={AssignmentTypeEnum.EEAssignment}
                              >
                                <AssignmentTypeCard
                                  title="W2"
                                  icon={
                                    <div className="holder">
                                      <span className="icon-wrapper">
                                        <span className="icon icon-hand" />
                                      </span>
                                    </div>
                                  }
                                />
                              </Radio.Button>
                            </Col>

                            <Col xs={8}>
                              <Radio.Button
                                className="radio-card-button"
                                value={AssignmentTypeEnum.ICAssignment}
                              >
                                <AssignmentTypeCard
                                  title="IC"
                                  icon={
                                    <div className="holder">
                                      <span className="icon-wrapper">
                                        <span className="icon icon-IC" />
                                      </span>
                                    </div>
                                  }
                                />
                              </Radio.Button>
                            </Col>

                            <Col xs={8}>
                              <Radio.Button
                                className="radio-card-button"
                                value={AssignmentTypeEnum.SCAssignment}
                              >
                                <AssignmentTypeCard
                                  title="Subcontract"
                                  icon={
                                    <div className="holder">
                                      <span className="icon-wrapper">
                                        <span className="icon icon-subcontract" />
                                      </span>
                                    </div>
                                  }
                                />
                              </Radio.Button>
                            </Col>
                          </Row>
                        </Radio.Group>
                      </FormItem>
                    </Col>
                  </Row>
                </Col>

                <Col xs={24} md={12}>
                  <div className={styles.entityDescription}>
                    {getDescription(formik.values)}
                  </div>
                </Col>
              </Row>
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
});
